import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import type {CampaignApplicationOpenSerializer} from "@/generated/Influencer";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {create} from "@/store/createStore";
import type {TNonFunctionKeys, TPaginated} from "@/store/types";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {debounce, omit, type DebouncedFuncLeading} from "lodash";
import qs from "qs";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TState = {
  searchParams: string;
  hasSubscription: boolean;
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  filters: TFilters;
  initialFilters: TInitialFilters;

  buildSearchParams(): string;
  setSubscription(hasSubscription: boolean): void;
  setFilters<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V): void;
  setInitialFilters(filters: TState["initialFilters"]): void;
  setSearchParams(searchParams: string): void;
  reset(): void;
};

export type TInitialFilters = {
  followers__min: number;
  followers__max: number;
  engagement__min: number;
  engagement__max: number;
  tiktok_last_period_video_count__min: number;
  tiktok_last_period_video_count__max: number;
  followers__histogram: {range: number; value: number}[];
  engagement__histogram: {range: number; value: number}[];
  tiktok_last_period_video_count__histogram: {range: number; value: number}[];
  rating__min: number;
  rating__max: number;
  rating__histogram: {range: number; value: number}[];
  swiss_regions: {
    id: number;
    name_de: string;
    name: string;
    country: string;
    country_de: string;
  }[];
  german_regions: {
    id: number;
    name_de: string;
    name: string;
    country: string;
    country_de: string;
  }[];
  high_density_regions: {
    id: number;
    name: string;
    influencer_count: number;
    country: string;
    country_de: string;
    name_de: string;
  }[];
};

export type TFilters = {
  engagement_start: number;
  engagement_end: number;
  tiktok_last_period_video_count_start: number;
  tiktok_last_period_video_count_end: number;
  followers_start: number;
  followers_end: number;
  rating_start: number;
  rating_end: number;
  new: boolean;
  no_review: boolean;
  audience_country: string | null;
  audience_region_id: number[];
  ordering: "-matching_score" | null;
};

const initialState = {
  searchParams: "",
  hasSubscription: false,
  initialFilters: {
    followers__min: 0,
    followers__max: 0,
    engagement__min: 0,
    engagement__max: 0,
    tiktok_last_period_video_count__min: 0,
    tiktok_last_period_video_count__max: 0,
    followers__histogram: [],
    engagement__histogram: [],
    tiktok_last_period_video_count__histogram: [],
    rating__min: 0,
    rating__max: 5,
    rating__histogram: [],
    swiss_regions: [],
    german_regions: [],
    high_density_regions: [],
  },
  filters: {
    engagement_start: 0,
    engagement_end: 0,
    tiktok_last_period_video_count_start: 0,
    tiktok_last_period_video_count_end: 0,
    followers_start: 0,
    followers_end: 0,
    rating_start: 0,
    rating_end: 5,
    new: false,
    no_review: false,
    audience_country: null,
    audience_region_id: [],
    ordering: null,
  },
} as Pick<TState, TNonFunctionKeys<TState>>;

const useApplicationFilterStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        debouncedSearchParams: debounce(() => {
          get().buildSearchParams();
        }, 1000),
        buildSearchParams: () => {
          const isTikTok = useCampaignManagementStore.getState().campaign.tiktok;
          const omittedFilters = omit(
            get().filters,
            isTikTok
              ? ["engagement_start", "engagement_end"]
              : ["tiktok_last_period_video_count_start", "tiktok_last_period_video_count_end"],
          );

          return qs.stringify(omittedFilters, {arrayFormat: "comma", skipNulls: true});
        },
        setSearchParams: searchParams =>
          set(
            state => {
              state.searchParams = searchParams;
            },
            false,
            "useApplicationFilterStore/buildSearchParams",
          ),
        setFilters: (key, value) => {
          get().debouncedSearchParams.cancel();
          set(
            state => {
              state.filters[key] = value;
            },
            false,
            "useApplicationFilterStore/setFilters",
          );
          let searchParams = get().buildSearchParams();
          let timeout = 1000;

          const cachedQuery = queryClient.getQueryData<
            InfiniteData<AxiosResponse<TPaginated<CampaignApplicationOpenSerializer>>>
          >(CAMPAIGN_KEYS.qCampaignOpenApplications(useCampaignManagementStore.getState().campaign.id!, searchParams));

          if (!!cachedQuery?.pages) {
            timeout = 0;
          }
          set(
            state => {
              state.debouncedSearchParams = debounce(() => {
                get().setSearchParams(searchParams);
              }, timeout);
            },
            false,
            "useApplicationFilterStore/setDebouncedSearchParams",
          );

          get().debouncedSearchParams();
        },
        setSubscription: value => {
          set(
            state => {
              state.hasSubscription = value;
            },
            false,
            "useApplicationFilterStore/setSubscription",
          );
          const params = get().buildSearchParams();
          get().setSearchParams(params);
        },
        setInitialFilters: filters => {
          set(
            state => {
              state.initialFilters = filters;
              state.filters.followers_start = filters.followers__min;
              state.filters.followers_end = filters.followers__max;
              state.filters.engagement_start = filters.engagement__min;
              state.filters.engagement_end = filters.engagement__max;
              state.filters.tiktok_last_period_video_count_start = filters.tiktok_last_period_video_count__min;
              state.filters.tiktok_last_period_video_count_end = filters.tiktok_last_period_video_count__max;
              state.filters.rating_start = filters.rating__min;
              state.filters.rating_end = filters.rating__max;
            },
            false,
            "useApplicationFilterStore/setInitialFilters",
          );

          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams);
        },
        reset: () => set(initialState, false, "useApplicationFilterStore/reset"),
      }),
      {name: "useApplicationFilterStore"},
    ),
  ),
);

export default useApplicationFilterStore;
